import { AdaptiveSelect } from '../../AdaptiveSelect';
import type { DataAttributeDto, FieldListDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { AddressInput } from './AddressInput';

/**
 * Il campo di un'entità (ADR 0208 § 4): il controllo lo dà la forma del campo, uguale in ogni scheda. Il valore resta un
 * testo, come lo vuole il server, che lo riporta nella forma del campo: sì/no «true» o «false», più scelte separate da «; »,
 * la quantità come «12.5 kg».
 */

const inputClass =
  'w-full rounded-[10px] border border-black/[0.08] bg-white px-3 py-2 text-sm outline-none transition-colors placeholder:text-ink/35 focus:border-accent/50 focus:ring-2 focus:ring-accent/15 disabled:bg-black/[0.03] disabled:text-ink/60';

/** Il valore salvato come testo per il campo: sì/no, più scelte e date come li legge il controllo. */
export function fieldText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.map(String).join('; ');
  return String(v);
}

export { fieldLabel } from '../../lib/field-values';

/** Gli elenchi dei campi di un tipo, in una richiesta sola. */
export function useFieldLists(attributes: Array<{ elenco?: string }>): Map<string, FieldListDto> {
  const keys = [...new Set(attributes.map((a) => a.elenco).filter((k): k is string => !!k))].sort();
  const q = useTenantQuery<FieldListDto[]>(['field-lists', keys.join(',')], `/model/lists?elenchi=${encodeURIComponent(keys.join(','))}`, { enabled: keys.length > 0 });
  return new Map((q.data ?? []).map((l) => [l.chiave, l]));
}

export function FieldInput({
  attr,
  value,
  values,
  lists,
  disabled,
  onChange,
}: {
  attr: DataAttributeDto;
  value: string;
  /** Tutti i valori dell'entità: un elenco dipendente filtra le voci con il valore del campo padre. */
  values: Record<string, string>;
  lists: Map<string, FieldListDto>;
  disabled?: boolean;
  onChange: (v: string) => void;
}) {
  const list = attr.elenco ? lists.get(attr.elenco) : undefined;
  const text = (type = 'text', extra: Record<string, string> = {}) => (
    <input className={inputClass} type={type} aria-label={attr.etichetta} value={value} disabled={disabled} placeholder={attr.descrizione ?? ''} onChange={(e) => onChange(e.target.value)} {...extra} />
  );
  switch (attr.forma) {
    case 'si_no':
      return (
        <div className="flex gap-4 py-2 text-sm" role="radiogroup" aria-label={attr.etichetta}>
          {[
            ['true', 'Sì'],
            ['false', 'No'],
            ['', 'Non so'],
          ].map(([v, l]) => (
            <label key={l} className="flex items-center gap-1.5">
              <input type="radio" name={attr.nome} checked={value === v} disabled={disabled} onChange={() => onChange(v!)} />
              {l}
            </label>
          ))}
        </div>
      );
    case 'scelta':
      return list && !list.libero ? <Choice attr={attr} list={list} value={value} parent={attr.dipende_da ? values[attr.dipende_da] : undefined} disabled={disabled} onChange={onChange} /> : text();
    case 'scelte':
      return list && !list.libero ? <Choices attr={attr} list={list} parent={attr.dipende_da ? values[attr.dipende_da] : undefined} value={value} disabled={disabled} onChange={onChange} /> : text();
    case 'quantita':
      return <Quantity list={list} value={value} disabled={disabled} onChange={onChange} />;
    case 'numero':
      return (
        <div className="flex items-center gap-2">
          {text('number', { step: 'any' })}
          {attr.unita && <span className="shrink-0 text-sm text-ink/60">{attr.unita}</span>}
        </div>
      );
    case 'importo':
      return (
        <div className="flex items-center gap-2">
          {text('number', { step: '0.01' })}
          <span className="shrink-0 text-sm text-ink/60">€</span>
        </div>
      );
    case 'ora':
      return text('time');
    case 'data_ora':
      return text('datetime-local');
    case 'anno':
      return text('number', { min: '1800', max: '2200', step: '1' });
    case 'esercizio':
      return text('text', { placeholder: '2025/2026', pattern: '\\d{4}/\\d{4}' });
    case 'testo_lungo':
      return <textarea className={`${inputClass} min-h-[72px]`} aria-label={attr.etichetta} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
    case 'indirizzo':
      return disabled ? text() : <AddressInput value={value} onPick={(a) => onChange(a.completo)} />;
    default:
      return text(attr.tipo === 'data' ? 'date' : attr.tipo === 'numero' ? 'number' : 'text');
  }
}

/** Una voce: tendina, con le voci del padre se l'elenco dipende da un altro campo, e «Altro…» se si può estendere. */
function Choice({ attr, list, value, parent, disabled, onChange }: { attr: DataAttributeDto; list: FieldListDto; value: string; parent: string | undefined; disabled?: boolean; onChange: (v: string) => void }) {
  const voci = list.voci.filter((v) => !v.per || !parent || v.per.includes(parent));
  const known = !value || list.voci.some((v) => v.valore === value);
  const [adding, setAdding] = useState(false);
  if (adding) return <AddOption list={list} parent={parent} onDone={(v) => (setAdding(false), v && onChange(v))} />;
  return (
    <div>
    <AdaptiveSelect
      className={inputClass}
      aria-label={attr.etichetta}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">—</option>
      {!known && <option value={value}>{value} (da correggere)</option>}
      {voci.map((v) => (
        <option key={v.valore} value={v.valore}>
          {v.valore !== v.etichetta && v.valore.length <= 6 ? `${v.valore} · ${v.etichetta}` : v.etichetta}
        </option>
      ))}
    </AdaptiveSelect>
    {list.estendibile && <button type="button" className="mt-1 text-xs text-accent disabled:opacity-50" disabled={disabled} onClick={() => setAdding(true)}>Altro…</button>}
    </div>
  );
}

/** Più voci: select multipla o autocomplete; il server riceve le stesse chiavi separate da «; ». */
function Choices({ attr, list, parent, value, disabled, onChange }: { attr: DataAttributeDto; list: FieldListDto; parent?: string; value: string; disabled?: boolean; onChange: (v: string) => void }) {
  const picked = value.split(/\s*;\s*/).filter(Boolean);
  const voci = list.voci.filter((v) => !v.per || !parent || v.per.includes(parent));
  const unknown = picked.filter((p) => !voci.some((v) => v.valore === p));
  return <AdaptiveSelect multiple className={inputClass} aria-label={attr.etichetta} value={picked} disabled={disabled}
    onChange={(e) => onChange(Array.from(e.currentTarget.selectedOptions, (o) => o.value).join('; '))}>
    {voci.map((v) => <option key={v.valore} value={v.valore}>{v.etichetta}</option>)}
    {unknown.map((v) => <option key={v} value={v}>{v} (da correggere)</option>)}
  </AdaptiveSelect>;
}

/** Numero e unità: le unità sono le voci dell'elenco del campo. Il valore è «12.5 kg». */
function Quantity({ list, value, disabled, onChange }: { list: FieldListDto | undefined; value: string; disabled?: boolean; onChange: (v: string) => void }) {
  const m = /^\s*([+-]?[\d.,]*)\s*(.*)$/.exec(value) ?? ['', '', ''];
  const [n, unit] = [m[1] ?? '', (m[2] ?? '').trim()];
  const join = (a: string, b: string) => onChange(a ? `${a}${b ? ` ${b}` : ''}` : '');
  return (
    <div className="flex gap-2">
      <input className={`${inputClass} min-w-0 flex-1`} type="number" step="any" aria-label="Quantità" value={n} disabled={disabled} onChange={(e) => join(e.target.value, unit)} />
      {list && !list.libero ? (
        <AdaptiveSelect className={`${inputClass.replace('w-full', '')} w-28 shrink-0`} aria-label="Unità" value={unit} disabled={disabled} onChange={(e) => join(n, e.target.value)}>
          <option value="">unità</option>
          {list.voci.map((v) => (
            <option key={v.valore} value={v.valore}>
              {v.valore}
            </option>
          ))}
        </AdaptiveSelect>
      ) : (
        <input className={`${inputClass.replace('w-full', '')} w-24 shrink-0`} placeholder="unità" value={unit} disabled={disabled} onChange={(e) => join(n, e.target.value)} />
      )}
    </div>
  );
}

/** «Altro…»: la voce nuova resta solo dell'azienda; se c'era già, scritta in altro modo, si sceglie quella. */
function AddOption({ list, parent, onDone }: { list: FieldListDto; parent: string | undefined; onDone: (v: string | null) => void }) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const add = useMutation({
    mutationFn: () => api<FieldListDto>(t(`/model/lists/${list.chiave}/options`), { body: { etichetta: label, ...(parent ? { per: [parent] } : {}) } }),
    onSuccess: async (l) => {
      await qc.invalidateQueries({ queryKey: ['tenant'], predicate: (q) => q.queryKey.includes('field-lists') });
      const fold = (s: string) => s.trim().toLowerCase();
      onDone(l.voci.find((v) => fold(v.etichetta) === fold(label))?.valore ?? l.voci.at(-1)?.valore ?? null);
    },
  });
  return (
    <div className="flex gap-2">
      <input className={inputClass} autoFocus placeholder={`Nuova voce di «${list.nome}»`} value={label} onChange={(e) => setLabel(e.target.value)} />
      <button type="button" className="shrink-0 rounded-[10px] bg-accent px-3 text-sm font-medium text-white disabled:opacity-50" disabled={!label.trim() || add.isPending} onClick={() => add.mutate()}>
        Aggiungi
      </button>
      <button type="button" className="shrink-0 px-2 text-sm text-ink/60" onClick={() => onDone(null)}>
        Annulla
      </button>
      {add.error && <span className="text-xs text-red-700">{errorMessage(add.error)}</span>}
    </div>
  );
}
